import { useCallback } from 'react';
import { toast } from 'sonner';
import { useConfirm } from '../../../hooks/useConfirm';
import { useAgentConversations } from '../../../contexts/AgentConversationsContext';
import type { Conversation } from '../../../models/agentConversation';
import { displayTitle } from './conversationPresentation';

// Rename, archive (with undo) and delete (with confirmation), with the toasts
// that report each outcome. Shared by the list rows and the thread header.
export function useConversationActions() {
  const agent = useAgentConversations();
  const confirm = useConfirm();

  const rename = useCallback(
    async (c: Conversation, title: string) => {
      try {
        await agent?.rename(c.id, title);
      } catch {
        toast.error('No se pudo renombrar la conversación. Inténtalo de nuevo.');
      }
    },
    [agent],
  );

  const toggleArchive = useCallback(
    async (c: Conversation) => {
      if (!agent) return;
      const archive = !c.archived;
      try {
        await agent.setArchived(c.id, archive);
        if (archive) {
          toast('Conversación archivada', {
            action: { label: 'Deshacer', onClick: () => agent.setArchived(c.id, false).catch(() => {}) },
          });
        } else {
          toast('Conversación desarchivada');
        }
      } catch {
        toast.error(
          archive
            ? 'No se pudo archivar la conversación. Inténtalo de nuevo.'
            : 'No se pudo desarchivar la conversación. Inténtalo de nuevo.',
        );
      }
    },
    [agent],
  );

  const remove = useCallback(
    async (c: Conversation) => {
      if (!agent) return;
      const ok = await confirm({
        title: `¿Eliminar «${displayTitle(c).text}»?`,
        description:
          'Se borran los mensajes. Las propuestas ya aplicadas se mantienen en el plan del paciente. No se puede deshacer.',
        confirmText: 'Eliminar conversación',
        destructive: true,
      });
      if (!ok) return;
      try {
        await agent.remove(c.id);
        toast('Conversación eliminada');
      } catch (err: any) {
        toast.error(
          err?.status === 409
            ? 'No se puede eliminar mientras Sissi responde. Inténtalo cuando termine.'
            : 'No se pudo eliminar la conversación. Inténtalo de nuevo.',
        );
      }
    },
    [agent, confirm],
  );

  return { rename, toggleArchive, remove };
}
